/**
 * Phone validation and E.164 formatting using libphonenumber-js.
 * This module requires libphonenumber-js to be installed (react_internal dependency).
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { parsePhoneNumberFromString as parseMobilePhoneNumberFromString } from 'libphonenumber-js/mobile';
/** Digits only from value. */
function digitsOnly(value) {
    return value.replace(/\D/g, '');
}
/**
 * Normalize input for parsing: libphonenumber-js expects + for international format.
 * If user didn't type +, prepend it so numbers without + still parse (e.g. 5145551234 → +15145551234).
 * Handles phone extensions properly by stripping them before normalization, then adding them back.
 */
function normalizeForParse(value) {
    const trimmed = value.trim();
    if (!trimmed) {
        return trimmed;
    }
    if (trimmed.startsWith('+')) {
        return trimmed;
    }
    // Extract extension if present (ext, ext., x, X, extension, ; formats)
    const extMatch = trimmed.match(/(\s+(ext\.?|x|extension)\s*[0-9]+|;[0-9]+)$/i);
    const extension = extMatch ? extMatch[0] : '';
    const phoneWithoutExt = extension ? trimmed.slice(0, -extension.length) : trimmed;
    const digits = digitsOnly(phoneWithoutExt);
    if (digits.length === 10 && /^[2-9]/.test(digits)) {
        return `+1${digits}${extension}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}${extension}`;
    }
    return `+${digits}${extension}`;
}
/**
 * Resolves to true when libphonenumber-js is ready (used to know when createPhoneValidator is usable).
 */
export function ensurePhoneUtilsLoaded() {
    return Promise.resolve(true);
}
/**
 * Returns E.164 format with extension preserved if valid; otherwise undefined.
 * Extensions are appended to the E.164 number (e.g., "+12124567890 ext 4724").
 */
export function getPhoneE164(value) {
    if (!value || !value.trim()) {
        return undefined;
    }
    const trimmed = value.trim();
    // Extract extension if present (ext, ext., x, X, extension, ; formats)
    const extMatch = trimmed.match(/(\s+(ext\.?|x|extension)\s*[0-9]+|;[0-9]+)$/i);
    const extension = extMatch ? extMatch[0] : '';
    const toParse = normalizeForParse(trimmed);
    const parsed = parsePhoneNumberFromString(toParse);
    if (!parsed?.isValid()) {
        return undefined;
    }
    const e164 = parsed.format('E.164');
    // Append extension back to E.164 number
    return extension ? `${e164}${extension}` : e164;
}
/**
 * Returns a validator function for use with Input's validate prop (e.g. type="tel").
 * @param translate - Optional translation function for localized messages
 */
export function createPhoneValidator(translate) {
    const t = translate || ((key) => key);
    return (value) => {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }
        const toParse = normalizeForParse(trimmed);
        const parsed = parsePhoneNumberFromString(toParse);
        if (!parsed) {
            return { valid: false, message: t('Not a valid phone number') };
        }
        const formatted = parsed.formatInternational();
        if (parsed.isValid()) {
            return { valid: true, message: `"${formatted}" ${t('is a valid phone number')}` };
        }
        return { valid: false, message: `"${formatted}" ${t('is not a valid phone number')}` };
    };
}
/**
 * Returns a validator function that only accepts mobile phone numbers.
 * For use with MFA SMS verification to ensure the number can receive text messages.
 * Uses the mobile metadata bundle which has enhanced mobile number detection.
 * @param translate - Optional translation function for localized messages
 */
export function createMobilePhoneValidator(translate) {
    const t = translate || ((key) => key);
    return (value) => {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }
        const toParse = normalizeForParse(trimmed);
        const parsed = parseMobilePhoneNumberFromString(toParse);
        if (!parsed) {
            return { valid: false, message: t('Not a valid phone number') };
        }
        const formatted = parsed.formatInternational();
        if (!parsed.isValid()) {
            return { valid: false, message: t('Only mobile phone numbers are accepted for SMS verification') };
        }
        // Check if the number is a mobile type
        const numberType = parsed.getType();
        const isMobile = numberType === 'MOBILE' || numberType === 'FIXED_LINE_OR_MOBILE';
        if (!isMobile) {
            return { valid: false, message: t('Only mobile phone numbers are accepted for SMS verification') };
        }
        return { valid: true, message: `"${formatted}" ${t('is a valid mobile phone number')}` };
    };
}
/**
 * Validates a phone number using libphonenumber-js.
 * Uses the same validation logic as MFA - auto-detects US numbers and validates internationally.
 * Supports phone extensions natively through libphonenumber-js (ext, ext., x, X, extension, ;, #).
 *
 * @param phoneValue - The phone number to validate (with optional extension)
 * @returns true if the phone number is valid, false otherwise
 *
 * @example
 * isValidPhone('+1-555-123-4567') // true - international format
 * isValidPhone('555-123-4567') // true - US number (auto-detected)
 * isValidPhone('5551234567') // true - 10-digit US number
 * isValidPhone('543414478700 ext 4724') // true - phone with extension
 * isValidPhone('555-123-4567 x123') // true - phone with x extension
 */
export function isValidPhone(phoneValue) {
    if (!phoneValue?.trim()) {
        return false;
    }
    const toParse = normalizeForParse(phoneValue.trim());
    const parsed = parsePhoneNumberFromString(toParse);
    return parsed?.isValid() ?? false;
}
